import { type RefObject, useEffect, useState } from "react";
import { create } from "zustand";
import { trackEvent } from "../libs/analyticsManager";
import { useLoginState } from "../studioxr/auth/LoginState";

// The one-time callout that points team users at the workspace switcher.
// Hidden for good once they click "Got it" or open the switcher: this session
// at once (store), this device (localStorage), every device (profile stamp).

export const switcherTipLocalKey = (uid: string) =>
  `workspaceSwitcherTipSeen:${uid}`;

export const readTipSeenLocally = (uid: string | null | undefined) => {
  if (!uid) return false;
  try {
    return !!localStorage.getItem(switcherTipLocalKey(uid));
  } catch {
    return false;
  }
};

export type SwitcherTipDismissReason = "got_it" | "opened_switcher";

/** Team users on /home pages, once permissions have settled, until seen. */
export function shouldShowSwitcherTip({
  enabled,
  uid,
  permissionsSettled,
  hasActiveTeam,
  seen,
  pathname,
}: {
  enabled: boolean;
  uid: string | null;
  /** LoginState finished loading permissions and the team-status read is in;
   *  before that `workspaces` can be Personal-only for a team member. */
  permissionsSettled: boolean;
  /** An active team workspace (inactive-only members can't switch into one). */
  hasActiveTeam: boolean;
  seen: boolean;
  pathname: string;
}): boolean {
  if (!enabled || !uid || seen || !permissionsSettled) return false;
  return hasActiveTeam && /^\/home(\/|$)/.test(pathname);
}

/** True while a modal covers `ref`'s element: an open MUI Modal (any dialog,
 *  menu or drawer) marks everything behind it aria-hidden. Watches only while
 *  `watch` is on. */
export function useCoveredByModal(
  ref: RefObject<HTMLElement>,
  watch: boolean,
): boolean {
  const [covered, setCovered] = useState(false);
  useEffect(() => {
    if (!watch) return undefined;
    const read = () =>
      setCovered(!!ref.current?.closest('[aria-hidden="true"]'));
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-hidden"],
    });
    return () => observer.disconnect();
  }, [ref, watch]);
  return watch && covered;
}

/** In-session state, keyed by uid so a same-tab account switch inherits nothing. */
export const useSwitcherTipStore = create<{
  seenUid: string | null;
  /** uid the Viewed event went out for: once per session, not per Bar mount. */
  viewedUid: string | null;
  markSeen: (uid: string) => void;
  markViewed: (uid: string) => void;
}>((set) => ({
  seenUid: null,
  viewedUid: null,
  markSeen: (uid) => set({ seenUid: uid }),
  markViewed: (uid) => set({ viewedUid: uid }),
}));

const seenOnProfile = (uid: string) => {
  const profile = useLoginState.getState().profile;
  return profile?.uid === uid && !!profile.workspaceSwitcherTipSeenAt;
};

// false = not written (offline, profile doc not loaded yet); retried with
// the same backoff as the SketchUp banner stamp.
async function stampProfile(uid: string) {
  let lastError: unknown;
  for (let attempt = 1; attempt <= 6; attempt++) {
    // Never stamp a different account that signed in during a retry gap.
    if (useLoginState.getState().profile?.uid !== uid) return;
    try {
      if (await useLoginState.getState().markWorkspaceSwitcherTipSeen()) return;
    } catch (error) {
      lastError = error;
    }
    if (attempt < 6)
      await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
  }
  console.error(
    "Failed to stamp the workspace switcher tip as seen",
    lastError,
  );
}

/** Hides the tip for good. A repeat call in the same session is a no-op.
 *  `reason` only when the callout was on screen: it's the Dismissed event's
 *  reason, so dismissals stay comparable with views. */
export function markSwitcherTipSeen(
  uid: string,
  reason?: SwitcherTipDismissReason,
) {
  const store = useSwitcherTipStore.getState();
  if (store.seenUid === uid) return;
  store.markSeen(uid);
  if (seenOnProfile(uid)) return;
  // Seen on this device before, but that profile stamp never landed.
  if (readTipSeenLocally(uid)) {
    void stampProfile(uid);
    return;
  }
  if (reason) trackEvent("Workspace Switcher - Tip Dismissed", { reason });
  try {
    localStorage.setItem(switcherTipLocalKey(uid), "1");
  } catch {
    // Private mode / blocked storage: the profile stamp still covers it.
  }
  void stampProfile(uid);
}
